import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import addIcon from '../assets/icons/add.svg';
import customersIcon from '../assets/icons/customers.svg';
import vehiclesIcon from '../assets/icons/vehicles.svg';
import transactionsIcon from '../assets/icons/transactions.svg';
import paymentsIcon from '../assets/icons/payments.svg';
import { useAuth } from '../hooks/useAuth';
import { useRentalApi } from '../hooks/useRentalApi';
import { API_BASE_URL } from '../config/api';
import UnreadMessagesWidget from './dashboard/UnreadMessagesWidget';
import PriorityTasksWidget from './dashboard/PriorityTasksWidget';
import LeadsWidget from './dashboard/LeadsWidget';
import WorkLogWidget from './dashboard/WorkLogWidget';
import RentalHeatmapWidget from './dashboard/RentalHeatmapWidget';

/**
 * Monthly Summary Card for Managers
 * Shows current month's performance metrics for their store
 */
const MonthlySummaryCard = ({ user }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchMonthlyMetrics = async () => {
      console.log('[MonthlySummaryCard] Component mounted');
      console.log('[MonthlySummaryCard] User:', user);
      console.log('[MonthlySummaryCard] User storeId:', user?.storeId);
      console.log('[MonthlySummaryCard] User store_id:', user?.store_id);
      console.log('[MonthlySummaryCard] API_BASE_URL:', API_BASE_URL);
      
      // Support both storeId and store_id
      const storeId = user?.storeId || user?.store_id;
      
      if (!storeId) {
        console.warn('[MonthlySummaryCard] No storeId found, skipping fetch');
        console.warn('[MonthlySummaryCard] User may need to log out and log back in to get storeId');
        setError('Please log out and log back in to view monthly summary');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        const token = localStorage.getItem('tegrat_auth_token');
        const response = await fetch(
          `${API_BASE_URL}/analytics/store/${storeId}/monthly?year=${year}&month=${month}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch monthly metrics');
        }

        const data = await response.json();
        setMetrics(data);
      } catch (err) {
        console.error('Error fetching monthly metrics:', err);
        setError('Failed to load monthly summary');
      } finally {
        setLoading(false);
      }
  };

  const handleRefresh = async () => {
    const storeId = user?.storeId || user?.store_id;
    if (!storeId) return;

    setRefreshing(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;

      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(
        `${API_BASE_URL}/analytics/refresh`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            store_id: storeId,
            year: year,
            month: month,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to refresh analytics');
      }

      // Refresh the data after cache update
      await fetchMonthlyMetrics();
    } catch (err) {
      console.error('Error refreshing analytics:', err);
      setError('Failed to refresh analytics');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMonthlyMetrics();
  }, [user]);

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-0">Loading monthly summary...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-4 border-warning">
        <Card.Header>
          <h3>Monthly Summary</h3>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-warning mb-0">
            {error}
            {error.includes('log out') && (
              <div className="mt-2">
                <small className="text-muted">
                  Your session was created before store assignment was added. 
                  Logging out and back in will refresh your permissions.
                </small>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!metrics) {
    return null;
  }

  const now = new Date();
  const monthName = now.toLocaleString('default', { month: 'long' });
  const year = now.getFullYear();

  const utilizationRate = metrics.total_fleet_days > 0
    ? ((metrics.total_days_rented / metrics.total_fleet_days) * 100).toFixed(1)
    : 0;

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Monthly Summary - {monthName} {year}</h3>
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Refreshing...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </>
          )}
        </Button>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={3}>
            <Card className="text-center border-0" style={{ backgroundColor: '#d1fae5' }}>
              <Card.Body className="py-2">
                <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Revenue
                </Card.Title>
                <h3 className="mb-0" style={{ color: '#10b981' }}>
                  ${parseFloat(metrics.total_revenue || 0).toFixed(2)}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Body className="py-2">
                <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Rentals
                </Card.Title>
                <h3 className="mb-0">{metrics.rental_count || 0}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Body className="py-2">
                <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Avg Duration
                </Card.Title>
                <h3 className="mb-0">
                  {parseFloat(metrics.avg_duration_days || 0).toFixed(1)} days
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-0" style={{ backgroundColor: '#ccfbf1' }}>
              <Card.Body className="py-2">
                <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Utilization
                </Card.Title>
                <h3 className="mb-0" style={{ color: utilizationRate >= 70 ? '#10b981' : '#0d9488' }}>
                  {utilizationRate}%
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

/**
 * Quick Actions card component - reusable for both admin and manager views
 */
const QuickActionsCard = ({ navigate }) => (
  <Card>
    <Card.Header>
      <h3>Quick Actions</h3>
    </Card.Header>
    <Card.Body>
      <div className="d-flex flex-wrap gap-2">
        <span className="domain-customers">
          <Button variant="primary" onClick={() => navigate('./customers', { state: { openAddForm: true } })}>
            <img src={addIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />
            <img src={customersIcon} alt="" className="icon-tab" />
            Add New Customer
          </Button>
        </span>
        <span className="domain-vehicles">
          <Button variant="primary" onClick={() => navigate('./vehicles', { state: { openAddForm: true } })}>
            <img src={addIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />
            <img src={vehiclesIcon} alt="" className="icon-tab" />
            Add New Vehicle
          </Button>
        </span>
        <span className="domain-transactions">
          <Button variant="primary" onClick={() => navigate('./transactions', { state: { openAddForm: true } })}>
            <img src={addIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />
            <img src={transactionsIcon} alt="" className="icon-tab" />
            Create Transaction
          </Button>
        </span>
        <span className="domain-payments">
          <Button variant="primary" onClick={() => navigate('./payments', { state: { openAddForm: true } })}>
            <img src={addIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />
            <img src={paymentsIcon} alt="" className="icon-tab" />
            Tender Payment
          </Button>
        </span>
      </div>
    </Card.Body>
  </Card>
);

/**
 * DashboardOverview component displays the main dashboard overview
 * with statistics and quick action buttons
 * For admins, shows a stores overview with metrics per store
 */
export function DashboardOverview() {
  const navigate = useNavigate();
  const { stats, loading, user: contextUser } = useOutletContext();
  const { user: authUser } = useAuth();
  const api = useRentalApi();

  // Use context user (refreshed) if available, fallback to auth user
  const user = contextUser || authUser;

  // Admin stores overview state
  const [stores, setStores] = useState([]);
  const [storeMetrics, setStoreMetrics] = useState({});
  const [loadingStores, setLoadingStores] = useState(false);
  const [storesError, setStoresError] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadStoresOverview();
    }
  }, [user]);

  const loadStoresOverview = async () => {
    setLoadingStores(true);
    setStoresError(null);

    try {
      // Fetch stores overview with all metrics from backend
      const storesData = await api.fetchAdminStoresOverview();

      // Convert the response to the format expected by the component
      const storesArray = storesData || [];
      const metricsObj = {};

      storesArray.forEach(store => {
        metricsObj[store.id] = {
          vehicles: store.vehicles,
          transactions: store.transactions
        };
      });

      setStores(storesArray);
      setStoreMetrics(metricsObj);
    } catch (err) {
      console.error('Error loading stores overview:', err);
      setStoresError('Failed to load stores overview.');
    } finally {
      setLoadingStores(false);
    }
  };

  // Admin-specific stores overview
  if (user?.role === 'admin') {
    if (loadingStores) {
      return (
        <Container fluid className="py-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading stores overview...</p>
          </div>
        </Container>
      );
    }

    if (storesError) {
      return (
        <Container fluid className="py-4">
          <div className="alert alert-danger">{storesError}</div>
          <Button variant="primary" onClick={loadStoresOverview}>
            Retry
          </Button>
        </Container>
      );
    }

    return (
      <Container fluid className="py-4">
        <div className="mb-4">
          <h2>Admin Overview - All Stores</h2>
        </div>

        {/* Communication and Task Widgets */}
        <Row className="mb-4">
          <Col md={6}>
            <UnreadMessagesWidget />
          </Col>
          <Col md={6}>
            <PriorityTasksWidget />
          </Col>
        </Row>

        {/* Work Log Widget */}
        <Row className="mb-4">
          <Col md={6}>
            <WorkLogWidget />
          </Col>
          <Col md={6}>
            <LeadsWidget />
          </Col>
        </Row>

        {/* Rental Activity Heatmap */}
        <Row className="mb-4">
          <Col md={12}>
            <RentalHeatmapWidget showStoreFilter stores={stores} />
          </Col>
        </Row>

        <div className="stores-list">
          {stores.map(store => {
            const metrics = storeMetrics[store.id] || {};
            const vehicles = metrics.vehicles || {};

            return (
              <Card key={store.id} className="mb-4">
                <Card.Body>
                  <h5 className="mb-3">
                    📍 {store.name || `Store ${store.id}`}
                  </h5>
                  <Row className="g-3">
                    <Col md={2}>
                      <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>Total</Card.Title>
                          <h3 className="mb-0">{vehicles.total || 0}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>Available</Card.Title>
                          <h3 className="mb-0" style={{ color: '#27ae60' }}>
                            {vehicles.available || 0}
                          </h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>Rented</Card.Title>
                          <h3 className="mb-0" style={{ color: '#e74c3c' }}>
                            {vehicles.rented || 0}
                          </h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>RTO</Card.Title>
                          <h3 className="mb-0" style={{ color: '#9b59b6' }}>
                            {vehicles.rto || 0}
                          </h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>Maintenance</Card.Title>
                          <h3 className="mb-0" style={{ color: '#f39c12' }}>
                            {vehicles.maintenance || 0}
                          </h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card className="text-center border-0" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Card.Title style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>Transactions</Card.Title>
                          <h3 className="mb-0">{metrics.transactions || 0}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>

        <QuickActionsCard navigate={navigate} />
      </Container>
    );
  }

  // Regular overview for managers/customers
  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  // Debug logging for user object
  console.log('[DashboardOverview] User object:', user);
  console.log('[DashboardOverview] User role:', user?.role);
  console.log('[DashboardOverview] User storeId:', user?.storeId);
  console.log('[DashboardOverview] User store_id:', user?.store_id);
  console.log('[DashboardOverview] All user keys:', user ? Object.keys(user) : 'no user');
  console.log('[DashboardOverview] Should show MonthlySummaryCard:', user?.role === 'manager');
  console.log('[DashboardOverview] Stats from outlet context:', stats);
  console.log('[DashboardOverview] Loading state:', loading);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Fleet Overview</h2>
        </Col>
      </Row>

      {/* Monthly Summary Card for Managers */}
      {user?.role === 'manager' && <MonthlySummaryCard user={user} />}

      {/* Rental Activity Heatmap */}
      {user?.role === 'manager' && (
        <Row className="mb-4">
          <Col md={12}>
            <RentalHeatmapWidget />
          </Col>
        </Row>
      )}

      {/* Communication and Task Widgets */}
      <Row className="mb-4">
        <Col md={6}>
          <UnreadMessagesWidget />
          <WorkLogWidget />
        </Col>
        <Col md={6}>
          <PriorityTasksWidget />
        </Col>
      </Row>

      {/* Website Leads Widget */}
      <Row className="mb-4">
        <Col md={12}>
          <LeadsWidget />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title style={{ fontSize: '0.85rem' }}>Total</Card.Title>
              <h2 className="mb-0">{stats?.totalVehicles || 0}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title style={{ fontSize: '0.85rem' }}>Available</Card.Title>
              <h2 className="mb-0" style={{ color: '#27ae60' }}>
                {stats?.availableVehicles || 0}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title style={{ fontSize: '0.85rem' }}>Rented</Card.Title>
              <h2 className="mb-0" style={{ color: '#e74c3c' }}>
                {stats?.rentedVehicles || 0}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title style={{ fontSize: '0.85rem' }}>Total Transactions</Card.Title>
              <h2 className="mb-0">{stats?.totalTransactions || 0}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <QuickActionsCard navigate={navigate} />
    </Container>
  );
}

export default DashboardOverview;
